import React, { memo } from 'react';
import { TFunction } from 'i18next';
import _noop from 'lodash/noop';
import _get from 'lodash/get';
import { useTranslation } from 'react-i18next';
import {
  FormTemplateQuestionField,
  FormTemplateQuestionFieldOptionGroupEnum,
  FormTemplateQuestionFieldsKindEnum,
} from 'graphql-common';
import SupportiveMessage, {
  SupportiveMessageColor,
  SupportiveMessageIconColor,
  SupportiveMessageIconType,
  SupportiveMessageType,
} from '@lib/components/SupportiveMessage/SupportiveMessage';
import Input from '@lib/components/Input/Input';
import { InputTypes } from '@lib/components/Input/enums';
import Select from '@lib/components/Select/Select';
import RadioButtonGroup, {
  RadioButtonWrapViews,
} from '@lib/components/RadioButtonGroup/RadioButtonGroup';
import CheckBoxGroup from '@lib/components/CheckBoxGroup/CheckBoxGroup';
import InputMinMax from '@lib/components/Input/InputMinMax';
import Rating from '@lib/components/Rating/Rating';
import Button, {
  ButtonSizes,
  ButtonTypes,
} from '@lib/components/Button/Button';
import DatePicker from '@lib/components/DateTimePicker/DatePicker';
import DateRangePicker from '@lib/components/DateTimePicker/DateRangePicker';
import DateTimePicker from '@lib/components/DateTimePicker/DateTimePicker';
import TimePicker from '@lib/components/DateTimePicker/TimePicker';
import TimeRangePicker from '@lib/components/DateTimePicker/TimeRangePicker';
import CheckboxButtonGrid from '@lib/components/CheckboxButtonGrid/CheckboxButtonGrid';
import { CheckboxButtonGroupOption } from '@lib/components/CheckboxButtonGrid/types';
import RadioButtonAsButton from '@lib/components/RadioButtonAsButton/RadioButtonAsButton';
import FormTemplateFieldNames from '@lib/enums/fieldNames/formTemplateFieldNames';
import Checklist from '@lib/components/Checklist/Checklist';

const defaultProps = {
  onChange: _noop,
  disabled: true,
};

type Props = Pick<
  FormTemplateQuestionField,
  | FormTemplateFieldNames.Kind
  | FormTemplateFieldNames.Options
  | FormTemplateFieldNames.Settings
  | FormTemplateFieldNames.Required
>;

interface ComponentProps extends Props {
  t: TFunction<'translation', undefined>;
}

function InputPreview({ t }: ComponentProps) {
  return (
    <Input
      type={InputTypes.text}
      label={t('single-line-value')}
      name={`${FormTemplateQuestionFieldsKindEnum.Input}-preview`}
      {...defaultProps}
    />
  );
}

function TextAreaPreview({ t }: ComponentProps) {
  return (
    <Input
      type={InputTypes.textarea}
      label={t('multi-line-value')}
      name={`${FormTemplateQuestionFieldsKindEnum.Input}-preview`}
      {...defaultProps}
    />
  );
}

function RadioButtonPreview({ options }: ComponentProps) {
  const preparedOptions = options.map(({ values }) => ({
    value: values[0],
    label: values[0],
  }));
  return (
    <RadioButtonGroup
      options={preparedOptions}
      value={undefined}
      name={FormTemplateQuestionFieldsKindEnum.RadioButton}
      {...defaultProps}
    />
  );
}

function CheckboxPreview({ options }: ComponentProps) {
  const preparedOptions = options.map(({ values }) => ({
    value: values[0],
    label: values[0],
  }));
  return (
    <CheckBoxGroup
      options={preparedOptions}
      value={undefined}
      name={FormTemplateQuestionFieldsKindEnum.Checkbox}
      {...defaultProps}
    />
  );
}

function SingleSelectPreview({ options, t }: ComponentProps) {
  const preparedOptions = options.map(({ values }) => ({
    value: values[0],
    label: values[0],
  }));
  return (
    <Select
      {...defaultProps}
      options={preparedOptions}
      label={t('choose-option')}
    />
  );
}

function YesNoPreview({ options }: ComponentProps) {
  const preparedOptions = options.map(({ group, values }) => ({
    value: group || '',
    label: values[0] || '',
    leftIcon:
      group === FormTemplateQuestionFieldOptionGroupEnum.Yes
        ? 'check_circle'
        : 'cancel',
  }));
  return (
    <RadioButtonGroup
      options={preparedOptions}
      value={undefined}
      name={FormTemplateQuestionFieldsKindEnum.RadioButton}
      RadioButtonComponent={RadioButtonAsButton}
      radioButtonComponentProps={{
        disabled: true,
        fullWidth: true,
      }}
      wrapView={RadioButtonWrapViews.list}
      {...defaultProps}
    />
  );
}

function NumberInputPreview({ t }: ComponentProps) {
  return (
    <Input
      {...defaultProps}
      type={InputTypes.number}
      label={t('number-input')}
      name={`${FormTemplateQuestionFieldsKindEnum.Input}-preview`}
    />
  );
}

function MinMaxPreview() {
  return <InputMinMax {...defaultProps} value={undefined} />;
}

function DatePreview({ t }: ComponentProps) {
  return (
    <DatePicker
      {...defaultProps}
      label={t('select-date')}
      type={InputTypes.date}
    />
  );
}

function DateRangePreview({ t }: ComponentProps) {
  return (
    <DateRangePicker
      {...defaultProps}
      label={t('select-date-range')}
      type={InputTypes.dateRange}
    />
  );
}

function TimePreview({ t }: ComponentProps) {
  return <TimePicker {...defaultProps} label={t('select-time')} />;
}

function TimeRangePreview({ t }: ComponentProps) {
  return <TimeRangePicker {...defaultProps} label={t('select-time-range')} />;
}

function DatetimePreview({ t }: ComponentProps) {
  return (
    <DateTimePicker
      {...defaultProps}
      label={t('select-date-and-time')}
      type={InputTypes.time}
    />
  );
}

function CheckboxButtonGridPreview(props: ComponentProps) {
  const options = _get(
    props,
    FormTemplateFieldNames.Options,
    [],
  ) as CheckboxButtonGroupOption[];
  const limitToOneRespPerRow = _get(
    props,
    [
      FormTemplateFieldNames.Settings,
      FormTemplateFieldNames.LimitToOneRespPerRow,
    ],
    false,
  ) as boolean;
  const limitToOneRespPerColumn = _get(
    props,
    [
      FormTemplateFieldNames.Settings,
      FormTemplateFieldNames.LimitToOneRespPerColumn,
    ],
    false,
  ) as boolean;
  return (
    <CheckboxButtonGrid
      options={options}
      {...defaultProps}
      limitToOneRespPerRow={limitToOneRespPerRow}
      limitToOneRespPerColumn={limitToOneRespPerColumn}
    />
  );
}

function ChecklistPreview(props: ComponentProps) {
  const { options } = props;
  const limitToOneResp = _get(
    props,
    [FormTemplateFieldNames.Settings, FormTemplateFieldNames.LimitToOneResp],
    false,
  ) as boolean;
  const preparedOptions = options.map(({ values, id }) => ({
    id,
    label: values[0],
    value: values[0],
  }));
  const value = [];
  return (
    <Checklist
      {...defaultProps}
      options={preparedOptions}
      value={value}
      name={FormTemplateQuestionFieldsKindEnum.Checklist}
      limitToOneResp={limitToOneResp}
    />
  );
}

function RatingPreview() {
  return (
    <Rating
      {...defaultProps}
      value={undefined}
      name={FormTemplateQuestionFieldsKindEnum.Rating}
    />
  );
}

function AttachmentPreview({ t, required = false }: ComponentProps) {
  return (
    <div>
      <Button
        buttonType={ButtonTypes.secondaryOutlined}
        buttonSize={ButtonSizes.large}
        buttonText={t('upload-file')}
        leftIcon="upload"
        disabled
      />
      {required && (
        <div style={{ marginTop: '16px' }}>
          <SupportiveMessage
            color={SupportiveMessageColor.Yellow}
            text={t('attachment-field-offline-text')}
          />
        </div>
      )}
    </div>
  );
}

function DecisionBoxPreview(props: ComponentProps) {
  const { t } = props;
  const text = _get(
    props,
    `${FormTemplateFieldNames.Options}.0.${FormTemplateFieldNames.Values}.0`,
    '',
  );
  const options = _get(props, FormTemplateFieldNames.Options, []);
  const preparedOptions = options.map(({ values }) => ({
    value: values[0],
    label: text || t('decision-box-message'),
  }));
  return (
    <CheckBoxGroup
      options={preparedOptions}
      value={undefined}
      name={FormTemplateQuestionFieldsKindEnum.DecisionBox}
      {...defaultProps}
    />
  );
}

function ImportantNoticePreview(props: ComponentProps) {
  const { t } = props;
  const text = _get(
    props,
    `${FormTemplateFieldNames.Options}.0.${FormTemplateFieldNames.Values}.0`,
    '',
  );
  return (
    <SupportiveMessage
      text={text || t('important-notice-message')}
      leftIcon="warning"
      color={SupportiveMessageColor.Yellow}
      iconColor={SupportiveMessageIconColor.Inner}
      iconType={SupportiveMessageIconType.FilledWithBorder}
      type={SupportiveMessageType.FilledWithBorder}
    />
  );
}

const componentsMap = {
  [FormTemplateQuestionFieldsKindEnum.Input]: InputPreview,
  [FormTemplateQuestionFieldsKindEnum.TextArea]: TextAreaPreview,
  [FormTemplateQuestionFieldsKindEnum.RadioButton]: RadioButtonPreview,
  [FormTemplateQuestionFieldsKindEnum.Checkbox]: CheckboxPreview,
  [FormTemplateQuestionFieldsKindEnum.SingleSelect]: SingleSelectPreview,
  [FormTemplateQuestionFieldsKindEnum.YesNo]: YesNoPreview,
  [FormTemplateQuestionFieldsKindEnum.NumberInput]: NumberInputPreview,
  [FormTemplateQuestionFieldsKindEnum.MinMax]: MinMaxPreview,
  [FormTemplateQuestionFieldsKindEnum.Date]: DatePreview,
  [FormTemplateQuestionFieldsKindEnum.DateRange]: DateRangePreview,
  [FormTemplateQuestionFieldsKindEnum.Time]: TimePreview,
  [FormTemplateQuestionFieldsKindEnum.TimeRange]: TimeRangePreview,
  [FormTemplateQuestionFieldsKindEnum.Datetime]: DatetimePreview,
  [FormTemplateQuestionFieldsKindEnum.CheckboxButtonGrid]:
    CheckboxButtonGridPreview,
  [FormTemplateQuestionFieldsKindEnum.Checklist]: ChecklistPreview,
  [FormTemplateQuestionFieldsKindEnum.Rating]: RatingPreview,
  [FormTemplateQuestionFieldsKindEnum.Attachment]: AttachmentPreview,
  [FormTemplateQuestionFieldsKindEnum.DecisionBox]: DecisionBoxPreview,
  [FormTemplateQuestionFieldsKindEnum.ImportantNotice]: ImportantNoticePreview,
  [FormTemplateQuestionFieldsKindEnum.Section]: undefined,
};

function QuestionFieldPreviewByKind(props: Props) {
  const { kind } = props;
  const { t } = useTranslation();
  const Component = componentsMap[kind];
  if (Component === undefined) return null;
  return <Component {...props} t={t} />;
}

export default memo(QuestionFieldPreviewByKind);
